import React, { useEffect, useState } from "react";
import {
  Table, Input, Button, Select, Space, Tag, Popconfirm,
  message, Modal, DatePicker, Form, Switch,
  Row,
  Col
} from "antd";
import {
  SearchOutlined, EditOutlined, DeleteOutlined,
  PlusOutlined, EyeOutlined
} from "@ant-design/icons";
import { Helmet } from "react-helmet-async";
import { apiCall } from "../../API/RestApi";
import { useSelector } from "react-redux";
import moment from "moment";
import dayjs from 'dayjs';

const { Option } = Select;
const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const Schedule = () => {
  const token = useSelector((state) => state.user.token);

  // State
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [shifts, setShifts] = useState([]);

  // Helper API request
  const requestAPI = async ({ method, url, data: body }) => {
    try {
      const res = await apiCall({
        method,
        url,
        data: { data: body, headers: { Authorization: `Bearer ${token}` } }
      });
      return res;
    } catch (err) {
      console.error(err);
      message.error("API request failed");
      return null;
    }
  };

  // Fetch shifts
  const fetchShifts = async () => {
    const res = await requestAPI({ method: "GET", url: "/shift?page=1&limit=1000" });
    if (res?.status === "success") setShifts(res.data.data);
  };

  // Fetch list
  const fetchData = async (page = meta.page, limit = meta.limit) => {
    setLoading(true);
    let url = `/schedule?page=${page}&limit=${limit}`;
    if (search) url += `&search=${search}`;
    const res = await requestAPI({ method: "GET", url });
    if (res?.status === "success") {
      setData(res.data.data);
      setMeta((prev) => ({ ...prev, total: res.data.total_data }));
    }
    setLoading(false);
  };

  // Fetch detail
  const fetchDetail = async (id) => {
    const res = await requestAPI({ method: "GET", url: `/schedule/${id}` });
    return res?.status === "success" ? res.data : null;
  };

  // Create / Update
  const saveSchedule = async (values) => {
    const payload = {
      schedule_name: values.schedule_name,
      effective_day: values.effective_day.format("YYYY-MM-DD"),
      shift_day: daysOfWeek.map((day) => ({
        shift_id: values[`shift_${day}`],
        dayoff: values[`dayoff_${day}`] || false,
        day_name: day
      })),
    };
    console.log(payload)
    const method = editingId ? "PUT" : "POST";
    const url = editingId ? `/schedule/${editingId}` : "/schedule";
    const res = await requestAPI({ method, url, data: payload });
    if (res?.status === "success") {
      message.success(`Schedule ${editingId ? "updated" : "created"} successfully`);
      setModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchData();
    }
  };

  // Delete
  const handleDelete = async (id) => {
    const res = await requestAPI({ method: "DELETE", url: `/schedule/${id}` });
    if (res?.status === "success") {
      message.success("Schedule deleted successfully");
      fetchData();
    }
  };

  // Action handlers
  const handleOpenCreate = () => {
    form.resetFields();
    setEditingId(null);
    setModalVisible(true);
  };

  const handleOpenEdit = async (id) => {
    form.resetFields();
    const detail = await fetchDetail(id);
    if (!detail) return;

    setEditingId(id);
    const cleanDate = dayjs(detail.effective_day.split("T")[0]);


    form.setFieldsValue({
      schedule_name: detail.schedule_name,
      effective_day: cleanDate,
      ...detail.shift_day.reduce((acc, s) => {
        acc[`shift_${s.day_name}`] = Number(s.shift_id);
        acc[`dayoff_${s.day_name}`] = s.dayoff;
        return acc;
      }, {}),
    });


    setModalVisible(true);
  };



  const handleOpenDetail = async (id) => {
    const detail = await fetchDetail(id);
    if (!detail) return;
    setDetailData(detail);
    setDetailModal(true);
  };

  // Table columns
  const columns = [
    { title: "No", render: (_, __, i) => (meta.page - 1) * meta.limit + i + 1 },
    { title: "Schedule Name", dataIndex: "schedule_name" },
    { title: "Effective Date", dataIndex: "effective_day", render: (t) => t ? moment(t).format("YYYY-MM-DD") : "-" },
    {
      title: "Action",
      render: (_, r) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => handleOpenDetail(r.schedule_id)} />
          <Button icon={<EditOutlined />} onClick={() => handleOpenEdit(r.schedule_id)} />
          <Popconfirm title="Delete this schedule?" onConfirm={() => handleDelete(r.schedule_id)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Effects
  useEffect(() => { fetchData(); }, [search, meta.page, meta.limit]);
  useEffect(() => { fetchShifts(); }, []);

  return (
    <div>
      <Helmet><title>Schedule</title></Helmet>
      <h2>Schedule</h2>

      {/* Top bar */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        {/* Kiri: Search */}
        <Space>
          <Input
            placeholder="Search by schedule name"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 300 }}
          />
          <Button type="primary" onClick={() => fetchData(1, meta.limit)}>Search</Button>
        </Space>

        {/* Kanan: Add Schedule */}
        <Button icon={<PlusOutlined />} onClick={handleOpenCreate}>Add Schedule</Button>
      </div>


      {/* Table */}
      <Table
        columns={columns}
        dataSource={data}
        rowKey="schedule_id"
        loading={loading}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          showSizeChanger: true,
        }}
        onChange={(p) => setMeta({ ...meta, page: p.current, limit: p.pageSize })}
      />

      {/* Create / Edit Modal */}
      <Modal
        title={editingId ? "Edit Schedule" : "Add Schedule"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.validateFields().then(saveSchedule)}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="schedule_name" label="Schedule Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="effective_day" label="Effective Date" rules={[{ required: true }]}>
            <DatePicker />
          </Form.Item>
          <h4>Shift Days</h4>
          {daysOfWeek.map((day) => (
            <Row key={day} gutter={16} style={{ marginBottom: 8, alignItems: "center" }}>
              {/* Kolom Hari */}
              <Col span={4}>
                <strong>{day}</strong>
              </Col>

              {/* Kolom Shift */}
              <Col span={14}>
                <Form.Item
                  name={`shift_${day}`}
                  rules={[{ required: true, message: "Please select a shift" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    showSearch
                    placeholder="Select shift"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? "")
                        .toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {shifts.map((s) => (
                      <Option key={s.shift_id} value={s.shift_id}>
                        {s.name} ({s.time_in} - {s.time_out})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* Kolom Day Off */}
              <Col span={6}>
                <Form.Item
                  name={`dayoff_${day}`}
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Switch checkedChildren="Day Off" unCheckedChildren="Work" />
                </Form.Item>
              </Col>
            </Row>
          ))}
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title="Schedule Detail"
        open={detailModal}
        onCancel={() => setDetailModal(false)}
        footer={null}
        width={700}
      >
        {detailData && (
          <>
            <p><strong>Schedule Name:</strong> {detailData.schedule_name}</p>
            <p><strong>Effective Day:</strong> {moment(detailData.effective_day).format("YYYY-MM-DD")}</p>
            <Table
              dataSource={detailData.shift_day}
              rowKey="shift_day_id"
              pagination={false}
              columns={[
                { title: "Day", dataIndex: "day_name" },
                {
                  title: "Shift",
                  dataIndex: "shift_id",
                  render: (id) => {
                    const shift = shifts.find(s => s.shift_id === id);
                    return shift ? `${shift.name} (${shift.time_in} - ${shift.time_out})` : `Shift ID: ${id}`;
                  }
                },
                {
                  title: "Day Off",
                  dataIndex: "dayoff",
                  render: (val) => val ? <Tag color="red">Yes</Tag> : <Tag color="green">No</Tag>
                }
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default Schedule;
